import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { clearCachedGets } from "@/lib/cachedGet";

const AuthContext = createContext(null);
const TOKEN_KEY = "rallypointgo_token";
const STACK_KEY = "rallypointgo_imp_stack"; // stack of {token, path} for nested impersonation
const DEVICE_KEY = "rallypointgo_device"; // trusted-device token for admin 2FA "remember this device"
export const CHOICE_PROMPT_KEY = "rallypointgo_choice_prompt"; // set on sign-in, consumed by ChoicePrompt

const getStack = () => { try { return JSON.parse(localStorage.getItem(STACK_KEY) || "[]"); } catch { return []; } };
const setStack = (s) => localStorage.setItem(STACK_KEY, JSON.stringify(s));

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=logged out, object=user
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(getStack().length > 0);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setImpersonating(getStack().length > 0);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const login = async (email, password, teamId) => {
    const deviceToken = localStorage.getItem(DEVICE_KEY) || undefined;
    const { data } = await api.post("/auth/login", { email, password, team_id: teamId, device_token: deviceToken });
    if (data.multiple) return { multiple: true, accounts: data.accounts };
    if (data.twofa_required) return { twofa: true, challengeId: data.challenge_id, enrolled: data.enrolled };
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.removeItem(STACK_KEY);
    sessionStorage.setItem(CHOICE_PROMPT_KEY, "1");
    setImpersonating(false);
    setUser(data.user);
    return { user: data.user };
  };

  const setup2fa = (challengeId) => api.post("/auth/2fa/setup", { challenge_id: challengeId }).then((r) => r.data);
  const sendEmailCode = (challengeId) => api.post("/auth/2fa/email", { challenge_id: challengeId }).then((r) => r.data);
  const resetAuthenticator = (challengeId, recoveryCode) => api.post("/auth/2fa/reset-authenticator", { challenge_id: challengeId, recovery_code: recoveryCode }).then((r) => r.data);

  const verify2fa = async (challengeId, code, method, remember) => {
    const { data } = await api.post("/auth/2fa/verify", {
      challenge_id: challengeId, code, method, remember_device: !!remember });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    if (data.device_token) localStorage.setItem(DEVICE_KEY, data.device_token);
    localStorage.removeItem(STACK_KEY);
    sessionStorage.setItem(CHOICE_PROMPT_KEY, "1");
    setImpersonating(false);
    if (data.recovery_codes && data.recovery_codes.length) {
      // Defer activating the session so the one-time backup-codes screen can render
      // (setting the user immediately would trigger PublicOnly to redirect off /login).
      return { user: data.user, recoveryCodes: data.recovery_codes };
    }
    setUser(data.user);
    return { user: data.user, recoveryRemaining: data.recovery_remaining };
  };

  const commitSession = (u) => setUser(u);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* best-effort */ }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(STACK_KEY);
    sessionStorage.removeItem(CHOICE_PROMPT_KEY);
    clearCachedGets();
    setImpersonating(false);
    setUser(false);
  };

  // Push the current session onto the stack, recording where to return to, then switch tokens.
  const pushSession = (returnPath, newToken) => {
    const stack = getStack();
    stack.push({ token: localStorage.getItem(TOKEN_KEY), path: returnPath });
    setStack(stack);
    localStorage.setItem(TOKEN_KEY, newToken);
    setImpersonating(true);
  };

  const impersonate = async (uid) => {
    const { data } = await api.post(`/admin/accounts/${uid}/impersonate`);
    pushSession("/admin/accounts", data.access_token);
    setUser(data.user);
    return data.user;
  };

  const viewAsCoach = async (coachId) => {
    const { data } = await api.post(`/staff/view-as/${coachId}`);
    pushSession("/staff/users", data.access_token);
    await loadMe();
    window.location.href = "/coach/dashboard";
  };

  const stopImpersonating = () => {
    const stack = getStack();
    const prev = stack.pop();
    if (prev) {
      setStack(stack);
      localStorage.setItem(TOKEN_KEY, prev.token);
      window.location.href = prev.path || "/";
    } else {
      window.location.href = "/";
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, impersonating, viewOnly: !!(user && user.view_only), login, logout, setup2fa, sendEmailCode, resetAuthenticator, verify2fa, commitSession, impersonate, viewAsCoach, stopImpersonating, refresh: loadMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
